import {
    createEmgAuth,
    type EmgAuthClaims,
    type EmgAuthClient,
    type EmgAuthLoginOptions,
} from '@emg/auth-client';

import { authConfig } from './authConfig';

/**
 * EAA-1730: web-content's OIDC behavior now comes from the shared `@emg/auth-client`
 * package — the one source of truth for login/renew/logout/session-end across portal,
 * web-content and (via tarball) the LMS. This file previously carried its own copy of
 * `createEmgAuth` (EAA-1762); that implementation moved into the package unchanged
 * (superset: `getAccessTokenSync` + `refreshToken` are part of the package API), so
 * every importer of `{ authClient }` keeps working with no call-site change.
 *
 * Re-exported here (instead of importing the package directly everywhere) so the app
 * keeps a single module-level `UserManager` instance — `auth.service.ts`,
 * `apiClient`/`sharedApiClient` and `main.tsx`'s `configureShared` all read through
 * this one `authClient`, so there is exactly one PKCE/session state per tab.
 */

export type { EmgAuthClaims, EmgAuthClient, EmgAuthLoginOptions };
export { createEmgAuth };

/**
 * EAA-1762 addendum: `createEmgAuth`'s `UserManager` calls `getUser()` on
 * construction and (via `automaticSilentRenew`) may then reach for the
 * configured authority's `.well-known/openid-configuration` — a real network
 * call, unconditionally, the moment this module loads. In `authConfig.mode
 * === 'password'` environments (UAT) there is no deployed Keycloak, so that
 * authority is the dev-fallback `localhost:8081` and every page load threw
 * failed fetches in the console with no user-visible effect (nothing in
 * password mode ever calls into `authClient` — see the `usesPasswordAuth`
 * branches in `auth.service.ts`/`authOperations.ts`). This stub skips
 * constructing the real client so password mode makes zero OIDC network
 * calls; every method is unreachable dead code in that mode, so it throws
 * loudly instead of pretending to succeed.
 */
function createUnusedAuthClient(): EmgAuthClient {
    const unreachable = (): never => {
        throw new Error(
            'authClient is unused in password mode (authConfig.mode === "password") — this call path should not be reachable.'
        );
    };
    return {
        login: unreachable,
        handleCallback: unreachable,
        getAccessToken: () => Promise.resolve(null),
        getAccessTokenSync: () => null,
        getClaims: () => Promise.resolve(null),
        refreshToken: () => Promise.resolve(null),
        logout: () => Promise.resolve(),
        onSessionEnded: () => () => undefined,
        capabilityResolver: () => ({ can: () => false }),
    };
}

/**
 * App-wide singleton. `redirectUri` is fixed to `${origin}/callback`; the default
 * `post_logout_redirect_uri` (`${origin}/login`) matches web-content's prior wiring.
 */
export const authClient: EmgAuthClient =
    authConfig.mode === 'password'
        ? createUnusedAuthClient()
        : createEmgAuth({
              authority: authConfig.authority,
              clientId: authConfig.clientId,
              redirectUri: `${window.location.origin}/callback`,
          });
